import { service } from '@ember/service';
import { set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { jwtDecode } from 'tio-common/utils/jwt-decode';
import SessionService from 'ember-simple-auth/services/session';
import type ApiService from '../api';
import type RoleView from 'tio-common/models/role-view';
import type RouterService from '@ember/routing/router-service';
import type { RouteInfo, RouteInfoWithAttributes } from '@ember/routing/router-service';

interface AuthSessionPayload {
  iat: number;
  exp: number;
  id: number;
  employeeIds: number[];
  roles: RoleView[];
}

type Data = {
  transient?: Record<string, unknown>;
  permanent?: Record<string, unknown>;
  authenticated?: {
    access_token?: string;
    routeAfterAuthentication?: string;
    noTransitionAfterAuth?: boolean;
  };
};

const warnBeforeExpirationMin = 3;

export default class AppSessionService extends SessionService<Data> {
  @service declare api: ApiService;
  @service declare router: RouterService;
  @service declare cable: import('tio-common/services/cable').default;

  /**
   * A Unix timestamp representing the time at which the session will expire.
   */
  @tracked expireAt = 0;

  /**
   * Whether the session is expiring soon. This is set to `true` during the last few minutes of the
   * session, as defined by the internal constant `warnBeforeExpirationMin`.
   */
  @tracked isSessionExpiring = false;

  /**
   * Returns a value that is deleted when the session is invalidated.
   *
   * @param {string} property The name of the property to get
   *
   * @returns {any} The value of the property
   */
  getTransientValue(property: string) {
    return this.data.transient?.[property];
  }

  /**
   * Sets a value that is deleted when the session is invalidated.
   *
   * @param {string} property The name of the property to set
   * @param {any} value The value to set
   */
  setTransientValue(property: string, value: unknown) {
    // NOTE: To ensure the value is persisted to local storage or a cookie, we
    //       need to set the entire `transient` object. Also, `this.set` is
    //       overridden in `SessionService`, so this is not a "classic" method.
    //       [twl 26.Jul.23]

    set(this, 'data.transient', {
      ...this.data.transient,
      [property]: value,
    });
  }

  /**
   * Returns a value that persists indefinitely.
   *
   * @param {string} property The name of the property to get
   *
   * @returns {any} The value of the property
   */
  getPermanentValue(property: string) {
    return this.data.permanent?.[property];
  }

  /**
   * Sets a value that persists indefinitely.
   *
   * @param {string} property The name of the property to set
   * @param {any} value The value to set
   */
  setPermanentValue(property: string, value: unknown) {
    // NOTE: To ensure the value is persisted to local storage or a cookie, we
    //       need to set the entire `permanent` object.  Also, `this.set` is
    //       overridden in `SessionService`, so this is not a "classic" method.
    //       [twl 26.Jul.23]

    set(this, 'data.permanent', {
      ...this.data.permanent,
      [property]: value,
    });
  }

  setup() {
    return super.setup().then(() => this.#configureSessionTimers());
  }

  handleAuthentication(routeAfterAuthentication: string) {
    // `noTransitionAfterAuth` is passed in the data parameter to `authenticate`
    // @ts-expect-error I think this was part of vue bridge and need to see if we can remove it
    const { noTransitionAfterAuth } = this.session.get('authenticated');
    // @ts-expect-error I think this was part of vue bridge and need to see if we can remove it
    this.data.authenticated.routeAfterAuthentication = routeAfterAuthentication;

    if (!noTransitionAfterAuth) {
      super.handleAuthentication(routeAfterAuthentication);
    }

    this.#configureSessionTimers();

    const accessToken = this.data.authenticated?.access_token;
    if (accessToken) {
      this.cable.updateToken(accessToken);
    }
  }

  handleInvalidation() {
    const logoutRoute = '/logout';

    // When the logout route is already managing the flow (e.g. SLO redirect),
    // skip the automatic transition to avoid a re-entrant loop.
    const isLoggingOut = this.data.transient?.['isLoggingOut'];

    set(this, 'data.transient', {});

    if (!isLoggingOut) {
      this.router.transitionTo(logoutRoute);
    }
  }

  /**
   * Saves the route to redirect to after the user is fully authenticated.
   * Uses sessionStorage to persist the route.
   *
   * @param {string | null} currentRoute The current route to save
   */
  setRouteAfterAuthentication(currentRoute?: string | null) {
    const route = currentRoute || this.#routeInfoUrl(this.attemptedTransition?.to);

    if (!!route && this.#isValidRoute(route)) {
      sessionStorage.setItem('transitionAfterAuth', route);
      // Store timestamp to handle stale redirects
      sessionStorage.setItem('transitionAfterAuthTimestamp', Date.now().toString());
    }
  }

  /**
   * Transitions to the route saved in sessionStorage after the user is fully authenticated.
   */
  transitionToRouteAfterAuthentication() {
    const route = sessionStorage.getItem('transitionAfterAuth');
    const timestamp = sessionStorage.getItem('transitionAfterAuthTimestamp');

    if (route) {
      // Check if stored route is stale (older than 1 hour)
      const isStale = timestamp && Date.now() - parseInt(timestamp) > 3600000;

      if (!isStale && this.#isValidRoute(route)) {
        this.router.transitionTo(route);
      }

      // Clean up storage
      sessionStorage.removeItem('transitionAfterAuth');
      sessionStorage.removeItem('transitionAfterAuthTimestamp');
    }
  }

  async renew() {
    // TODO: Think about using an endpoint that isn't SAML-specific. This technically works, but
    //       it's overloading the purpose of this endpoint. [twl 18.Oct.23]
    const tokenInfo = await this.api.call('/saml/token_exchange');

    await this.authenticate('authenticator:saml', tokenInfo);

    this.#configureSessionTimers();

    const refreshedToken = this.data.authenticated?.access_token;
    if (refreshedToken) {
      this.cable.updateToken(refreshedToken);
    }
  }

  /**
   * Parses attemptedTransition.to object to get the route name and query params.
   * most of this logic is borrowed from this thread: https://discord.com/channels/480462759797063690/496695347582861334/1267497942877470770
   *
   * @param {RouteInfo | RouteInfoWithAttributes,} routeInfo The route info object from attemptedTransition
   */
  #routeInfoUrl(routeInfo?: RouteInfo | RouteInfoWithAttributes) {
    if (!routeInfo) return;

    try {
      const targetRoute = routeInfo.name;
      const allRouteParamValues: unknown[] = [];
      let allRouteQueryParams = {};

      // @ts-expect-error: not sure on this one, its expecting to return a boolean but breaks when it return anything. jpc
      routeInfo.find((info) => {
        info.paramNames.forEach((paramName: string) => {
          const paramValue = info.params?.[paramName];
          if (paramValue) {
            allRouteParamValues.push(paramValue);
          }
          allRouteQueryParams = {
            ...allRouteQueryParams,
            ...info.queryParams,
          };
        });
      });

      return this.router.urlFor(targetRoute, ...allRouteParamValues, {
        queryParams: allRouteQueryParams,
      });
    } catch (error) {
      console.error('Error generating route URL:', error);
      return undefined;
    }
  }

  #configureSessionTimers() {
    // @ts-expect-error: this is a dynamic property
    const { access_token } = this.session.get('authenticated');
    if (!access_token) {
      return;
    }
    const payload: AuthSessionPayload = jwtDecode(access_token);

    this.isSessionExpiring = false;
    this.expireAt = payload.exp * 1000;

    // `#setTimer` triggers immediately if the second parameter is in the past, so set `expire`
    // first, so we don't set `isSessionExpiring` if the session is already expired
    this.#setTimer('expire', this.expireAt, () => {
      if (this.isAuthenticated) {
        console.debug('[service:session] Session expired');
        const currentRoute = this.router.currentURL;
        if (this.#isValidRoute(currentRoute)) {
          this.setRouteAfterAuthentication(currentRoute);
        }
        this.handleInvalidation();
      }
    });
    this.#setTimer('warn', this.expireAt - warnBeforeExpirationMin * 60 * 1000, () => {
      if (this.isAuthenticated) {
        console.debug(
          `[service:session] Session expires in ${warnBeforeExpirationMin} min at ${new Date(this.expireAt)}`
        );
        this.isSessionExpiring = true;
      }
    });
  }

  #setTimer(type: string, timestamp: number, callback: () => void) {
    const idProperty = `${type}TimerId`;
    const delay = timestamp - Date.now();
    // @ts-expect-error: this is a dynamic property
    if (this[idProperty]) {
      // @ts-expect-error: this is a dynamic property
      clearTimeout(this[idProperty]);
    }

    if (delay > 0) {
      // @ts-expect-error: this is a dynamic property
      this[idProperty] = setTimeout(callback, delay);
    } else {
      callback();
    }
  }

  #isValidRoute(route: string | null | undefined): boolean {
    if (!route) return false;

    const invalidPaths = ['/logged-out', '/logout', '/', '/login'];
    return !invalidPaths.includes(route) && !route.includes('undefined') && route.startsWith('/');
  }
}
